import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { MatDialog, MatSelect, MatAutocomplete } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { PartPackageService, ProductTypeValueService } from '@app/api/api';
import { environment } from 'environments/environment';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'meu-part-package-detail',
  templateUrl: './part-package-detail.component.html',
  styleUrls: ['./part-package-detail.component.scss'],
})
export class PartPackageDetailComponent implements OnInit {
  packageId: string = '';
  packageInfo: any = {};
  lstItems: any = [];
  lstProducts: any[] = [];
  total: number = 0;
  totalPrice: number = 0;
  pageSize = 10;
  pageIndex = 1;
  host: any;
  showAddForm = false;
  showNotifyForm = false;
  isNotifying = false;
  editingItemId: string | null = null;
  pageSizeOptions: number[] = [5, 10, 20, 100];
  itemSearchValue: string = '';

  // Currently selected product (with type_values) - used to pick classification when adding
  selectedProduct: any = null;
  // Classification list of the currently selected product
  selectedProductTypeValues: any[] = [];

  displayedColumns: string[] = ['Index', 'Image', 'ProductName', 'ProductCode', 'ProductType', 'Price', 'Quantity', 'Note', 'Actions'];

  // Product pagination for autocomplete
  productPage = 1;
  productPageSize = 50;
  productTotalPages = 1;
  productLoading = false;
  productFilter = '';
  productScrollBound: (event: any) => void = () => {};

  addItemForm = new FormGroup({
    product_id: new FormControl('', [Validators.required]),
    quantity: new FormControl(1, [Validators.required, Validators.min(1)]),
    is_required: new FormControl(false),
    sort_order: new FormControl(0),
    note: new FormControl(''),
    product_type_id: new FormControl(null),
    product_type_text: new FormControl(null),
  });

  editItemForm = new FormGroup({
    quantity: new FormControl(1, [Validators.required, Validators.min(1)]),
    is_required: new FormControl(false),
    sort_order: new FormControl(0),
    note: new FormControl(''),
    product_type_id: new FormControl(null),
    product_type_text: new FormControl(null),
  });

  notifyForm = new FormGroup({
    title: new FormControl('', [Validators.required]),
    message: new FormControl('', [Validators.required]),
    url: new FormControl(''),
  });

  productSearchCtrl = new FormControl('');

  @ViewChild('auto', { static: false }) autoComplete!: MatAutocomplete;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private partPackageService: PartPackageService,
    private productTypeValueService: ProductTypeValueService
  ) {}

  ngOnInit() {
    this.packageId = this.route.snapshot.paramMap.get('packageId') || '';
    this.host = environment.backendhost;
    this.loadPackageInfo();
    this.loadItems();
    this.loadProducts();

    this.productSearchCtrl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value) => {
        if (typeof value === 'string') {
          this.addItemForm.patchValue({ product_id: '' });
          this.loadProducts(value);
        }
      });
  }

  loadPackageInfo() {
    this.partPackageService.apiPartPackagesIdGet(this.packageId).subscribe((res) => {
      if (res.success) {
        this.packageInfo = res.data.package || res.data;
        this.ref.detectChanges();
      }
    });
  }

  loadItems() {
    const filter = this.itemSearchValue ? `(name)@=*${this.itemSearchValue}` : '';
    this.partPackageService
      .apiPartPackagesIdItemsGet(this.packageId, filter, '', this.pageIndex, this.pageSize)
      .subscribe((res) => {
        if (res.success) {
          this.lstItems = res.data.collection;
          this.total = res.data.total;
          this.ref.detectChanges();
          this.loadTotalPrice();
        } else {
          this.toastr.error(res.message);
        }
      });
  }

  loadTotalPrice() {
    if (this.total <= this.lstItems.length) {
      this.totalPrice = this.calcTotalFromItems(this.lstItems);
      this.ref.detectChanges();
      return;
    }
    this.partPackageService
      .apiPartPackagesIdItemsGet(this.packageId, '', '', 1, this.total)
      .subscribe((res) => {
        if (res.success) {
          this.totalPrice = this.calcTotalFromItems(res.data.collection || []);
          this.ref.detectChanges();
        }
      });
  }

  private calcTotalFromItems(items: any[]): number {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, element) => {
      const unitPrice = this.getItemPrice(element);
      const pkgItem = (element && element.equipment_part_package_item) ? element.equipment_part_package_item : null;
      const qty = Number(pkgItem ? pkgItem.quantity : 1) || 1;
      return sum + unitPrice * qty;
    }, 0);
  }

  onItemSearch(value: string) {
    this.itemSearchValue = value;
    this.pageIndex = 1;
    this.loadItems();
  }

  loadProducts(search?: any) {
    const searchText = typeof search === 'string' ? search.trim() : '';
    // Search by name / sapatco_code / product_code / sku (use @= for contains match)
    this.productFilter = searchText ? 'name|sapatco_code|product_code|sku@=' + searchText : '';
    this.productPage = 1;
    this.productLoading = true;
    // Note: do NOT clear lstProducts here — clearing it closes the mat-autocomplete panel.
    // The panel stays open with old results while new ones load, then refreshes when API returns.
    this.productTypeValueService
      .apiFullProductsGet(this.productFilter, '', this.productPage, this.productPageSize)
      .subscribe((res) => {
        if (res.success) {
          // fullProducts API returns { product: {...}, type_values: [...] }
          // Keep type_values intact to pick classification when adding to the package
          this.lstProducts = (res.data.collection || []).map((item: any) => {
            if (item && item.product) {
              return {
                ...item.product,
                type_values: item.type_values || []
              };
            }
            return item;
          });
          this.productTotalPages = Math.ceil((res.data.total || 0) / this.productPageSize);
          this.ref.detectChanges();
        }
        this.productLoading = false;
      });
  }

  loadNextProductPage() {
    if (this.productLoading || this.productPage >= this.productTotalPages) return;
    this.productLoading = true;
    this.productPage++;
    this.productTypeValueService
      .apiFullProductsGet(this.productFilter, '', this.productPage, this.productPageSize)
      .subscribe((res) => {
        if (res.success && res.data && res.data.collection) {
          this.productTotalPages = Math.ceil((res.data.total || 0) / this.productPageSize);
          const newProducts: any[] = (res.data.collection || []).map((item: any) => {
            if (item && item.product) {
              return {
                ...item.product,
                type_values: item.type_values || []
              };
            }
            return item;
          });
          newProducts.forEach((newP: any) => {
            if (!this.lstProducts.some((p) => p.id === newP.id)) {
              this.lstProducts.push(newP);
            }
          });
          this.lstProducts = this.lstProducts.slice();
          this.ref.detectChanges();
        }
        this.productLoading = false;
      });
  }

  onProductPanelScroll(event: any) {
    const target = event.target;
    if (!target) return;
    const threshold = 200;
    const nearBottom = target.scrollHeight - target.clientHeight - target.scrollTop <= threshold;
    if (nearBottom) {
      this.loadNextProductPage();
    }
  }

  onProductAutocompleteOpened() {
    setTimeout(() => {
      const panel = this.autoComplete && this.autoComplete.panel
        ? this.autoComplete.panel.nativeElement
        : null;
      if (panel) {
        if (this.productScrollBound) {
          panel.removeEventListener('scroll', this.productScrollBound);
        }
        this.productScrollBound = this.onProductPanelScroll.bind(this);
        panel.addEventListener('scroll', this.productScrollBound);
      }
    }, 100);
  }

  toggleAddForm() {
    this.showAddForm = !this.showAddForm;
    if (!this.showAddForm) {
      this.addItemForm.reset({ quantity: 1, is_required: false, sort_order: 0, note: '', product_type_id: null, product_type_text: null });
      this.productSearchCtrl.setValue('');
      this.selectedProduct = null;
      this.selectedProductTypeValues = [];
    }
  }

  toggleNotifyForm() {
    this.showNotifyForm = !this.showNotifyForm;
    if (this.showNotifyForm && this.packageInfo) {
      const defaultTitle = this.packageInfo.name || '';
      const defaultMsg = this.packageInfo.description || this.packageInfo.name || '';
      this.notifyForm.patchValue({
        title: defaultTitle,
        message: defaultMsg,
        url: '',
      });
    }
  }

  sendNotify() {
    if (this.notifyForm.invalid) {
      Object.keys(this.notifyForm.controls).forEach((key) => {
        const ctrl = this.notifyForm.get(key);
        if (ctrl) {
          ctrl.markAsTouched();
        }
      });
      return;
    }
    const payload = {
      equipmentModelId: this.packageInfo.equipment_model_id,
      packageId: this.packageInfo.id,
      title: this.notifyForm.value.title,
      message: this.notifyForm.value.message,
      url: this.notifyForm.value.url || '',
    };
    this.isNotifying = true;
    this.partPackageService.apiPartPackagesNotifyPost(payload).subscribe(
      (res) => {
        this.isNotifying = false;
        if (res.success) {
          this.toastr.success('Gửi thông báo thành công');
          this.showNotifyForm = false;
          this.notifyForm.reset();
        } else {
          this.toastr.error(res.message || 'Gửi thông báo thất bại');
        }
        this.ref.detectChanges();
      },
      (err) => {
        this.isNotifying = false;
        this.toastr.error('Gửi thông báo thất bại');
        this.ref.detectChanges();
      }
    );
  }

  deletePrefix(prefixMappingId: string, prefixCode: string) {
    const title = 'Xóa tiền tố khỏi gói';
    const description = `Bạn có chắc chắn muốn xóa tiền tố "${prefixCode}" khỏi gói này?`;
    const waitDesciption = 'Đang xử lý...';
    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;
      this.partPackageService.apiPartPackagePrefixIdDelete(prefixMappingId).subscribe((res) => {
        if (res.success) {
          this.toastr.success('Xoá tiền tố khỏi gói thành công');
          this.loadPackageInfo();
        } else {
          this.toastr.error(res.message);
        }
      });
    });
  }

  addItem() {
    if (this.addItemForm.invalid) {
      Object.keys(this.addItemForm.controls).forEach((key) => {
        const ctrl = this.addItemForm.get(key);
        if (ctrl) {
          ctrl.markAsTouched();
        }
      });
      return;
    }
    // product_type_id and product_type_text:
    // - Product WITH classification: pass id + text of the selected classification
    // - Product WITHOUT classification: pass null for both
    const payload = {
      package_id: this.packageId,
      product_id: this.addItemForm.value.product_id,
      quantity: this.addItemForm.value.quantity,
      is_required: this.addItemForm.value.is_required,
      sort_order: this.addItemForm.value.sort_order,
      note: this.addItemForm.value.note,
      product_type_id: this.addItemForm.value.product_type_id || null,
      product_type_text: this.addItemForm.value.product_type_text || null,
    };
    this.partPackageService.apiPartPackageItemsPost(payload).subscribe((res) => {
      if (res.success) {
        this.toastr.success('Thêm phụ tùng vào gói thành công');
        // Keep the form open to add more products - only reset the input
        this.addItemForm.reset({ quantity: 1, is_required: false, sort_order: 0, note: '', product_type_id: null, product_type_text: null });
        this.productSearchCtrl.setValue('');
        this.selectedProduct = null;
        this.selectedProductTypeValues = [];
        this.loadItems();
      } else {
        this.toastr.error(res.message);
      }
    });
  }

  startEditItem(item: any) {
    const pkgItem = item && item.equipment_part_package_item ? item.equipment_part_package_item : null;
    this.editingItemId = pkgItem ? pkgItem.id : null;
    if (!this.editingItemId) return;
    // note may be inside equipment_part_package_item or at the top level of item
    const noteValue = pkgItem.note || item.note || '';
    this.editItemForm.patchValue({
      quantity: pkgItem.quantity,
      is_required: pkgItem.is_required,
      sort_order: pkgItem.sort_order || 0,
      note: noteValue,
      // Keep the existing classification (if any)
      product_type_id: pkgItem.product_type_id || null,
      product_type_text: pkgItem.product_type_text || null,
    });
    this.ref.detectChanges();
  }

  cancelEditItem() {
    this.editingItemId = null;
    this.editItemForm.reset({ quantity: 1, is_required: false, sort_order: 0, note: '', product_type_id: null, product_type_text: null });
    this.ref.detectChanges();
  }

  saveEditItem(item: any) {
    if (this.editItemForm.invalid) {
      Object.keys(this.editItemForm.controls).forEach((key) => {
        const ctrl = this.editItemForm.get(key);
        if (ctrl) {
          ctrl.markAsTouched();
        }
      });
      return;
    }
    const itemId = this.editingItemId || '';
    if (!itemId) return;
    // Payload per MD section 7: includes product_type_id and product_type_text
    // - Product WITH classification: pass id + text
    // - Product WITHOUT classification: pass null for both
    const payload = {
      package_id: this.packageId,
      product_id: (item.equipment_part_package_item && item.equipment_part_package_item.product_id) || (item.product && item.product.id),
      quantity: this.editItemForm.value.quantity,
      is_required: this.editItemForm.value.is_required,
      sort_order: this.editItemForm.value.sort_order,
      note: this.editItemForm.value.note,
      product_type_id: this.editItemForm.value.product_type_id || null,
      product_type_text: this.editItemForm.value.product_type_text || null,
    };
    this.partPackageService.apiPartPackageItemsIdPut(itemId, payload).subscribe((res) => {
      if (res.success) {
        this.toastr.success('Cập nhật phụ tùng thành công');
        this.cancelEditItem();
        this.loadItems();
      } else {
        this.toastr.error(res.message);
      }
    });
  }

  onProductSelected(event: any) {
    const product = event.option.value;
    this.selectedProduct = product;
    this.selectedProductTypeValues = (product && product.type_values) ? product.type_values : [];
    this.addItemForm.patchValue({
      product_id: product.id,
      product_type_id: null,
      product_type_text: null,
    });
    this.ref.detectChanges();
  }

  /**
   * When the user picks a classification in the dropdown
   * product_type_text per MD: "MODEL: SHINKO" (group_name: extend_type_names)
   * Here we use extend_type_names directly because the fullProducts API returns extend_type_names
   */
  onTypeValueChange(typeValueId: string) {
    if (!typeValueId) {
      this.addItemForm.patchValue({ product_type_id: null, product_type_text: null });
      this.ref.detectChanges();
      return;
    }
    const tv = this.selectedProductTypeValues.find((t) => t.id === typeValueId);
    if (tv) {
      this.addItemForm.patchValue({
        product_type_id: tv.id,
        product_type_text: tv.extend_type_names || null,
      });
    }
    this.ref.detectChanges();
  }

  /**
   * Get the unit price of an item in the table:
   * - If the product HAS a classification (product_type_value): use price, fallback to origin_price if price=0
   * - If the product has NO classification: use product.price, fallback to product.origin_price if price=0
   * Rule: prefer price; if price = 0, use origin_price
   */
  getItemPrice(element: any): number {
    const product = element && element.product ? element.product : null;
    if (!product) return 0;

    // Has classification -> take from product_type_value
    if (product.product_type_value) {
      const ptv = product.product_type_value;
      const price = Number(ptv.price) || 0;
      if (price > 0) return price;
      return Number(ptv.origin_price) || 0;
    }

    // No classification -> take from product
    const price = Number(product.price) || 0;
    if (price > 0) return price;
    return Number(product.origin_price) || 0;
  }

  /**
   * Total price of all items in the package = unit price * quantity
   */
  getTotalPrice(): number {
    return this.totalPrice;
  }

  displayFn(product: any): string {
    return product ? `${product.name} (${product.product_code})` : '';
  }

  /**
   * Format price: 250000 -> "250.000 đ"
   * Used instead of DecimalPipe to avoid unregistered locale errors
   */
  formatPrice(value: number): string {
    const n = Number(value) || 0;
    return n.toLocaleString('vi-VN') + ' đ';
  }

  deleteItem(itemId: string) {
    const title = 'Xóa phụ tùng khỏi gói';
    const description = 'Bạn có chắc chắn muốn xóa phụ tùng này khỏi gói?';
    const waitDesciption = 'Đang xử lý...';
    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;
      this.partPackageService.apiPartPackageItemsIdDelete(itemId).subscribe((res) => {
        if (res.success) {
          this.toastr.success('Xoá phụ tùng khỏi gói thành công');
          this.loadItems();
        } else {
          this.toastr.error(res.message);
        }
      });
    });
  }

  onPageChange(pageNumber: number) {
    this.pageIndex = pageNumber;
    this.loadItems();
    window.scrollTo(0, 0);
  }

  onPageSizeChange(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.loadItems();
  }


  trackById(index: number, item: any): any {
    return item ? item.id : undefined;
  }

  /**
   * Build full image URL from a relative path returned by the API.
   * - Absolute URLs (http/https) and local assets are returned as-is.
   * - Relative paths (e.g. /public/product_images/xxx.jpg) are prefixed with backendhost.
   */
  getFullImagePath(path: string): string {
    if (!path) return '/assets/media/products/placeholder.png';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('assets/')) {
      return path;
    }
    const host = environment.backendhost || '';
    const cleanHost = host.endsWith('/') ? host.slice(0, -1) : host;
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    return cleanHost + cleanPath;
  }

  goBack() {
    this.router.navigate(['/admin/part-package-management']);
  }
}
